import { Component, OnInit, ViewChild, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '../service/dashboard.service';
import { MatTableDataSource, MatPaginator } from '@angular/material';
import { ExportService } from '../service/quote-export.service';

@Component({
  selector: 'app-agtransactions',
  templateUrl: './agtransactions.component.html',
  styleUrls: ['./agtransactions.component.scss']
})
export class AgtransactionsComponent implements OnInit {

  userdata;
  transactions: any[] = [];
  filteredTransactions: any[] = [];
  pagedTransactions: any[] = [];

  isLoading: boolean = false;
  isRefreshing: boolean = false;

  // ── Export dropdown ────────────────────────────────────
  showExportMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const wrap = this.elRef.nativeElement.querySelector('.export-wrap');
    if (this.showExportMenu && wrap && !wrap.contains(event.target)) {
      this.showExportMenu = false;
    }
  }

  // search / filter
  searchQuery: string = '';
  selectedStatusFilter: string = '';
  selectedTypeFilter: string = '';

  // pagination
  currentPage: number = 0;
  pageSize: number = 5;
  totalItems: number = 0;
  totalPages: number = 0;

  transactionSum: string = 'GHS 0.00';

  // ── Transaction detail modal ────────────────────────────────
  selectedTransaction: any = null;

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.selectedTransaction) { this.closeTransactionModal(); }
  }

  viewTransaction(t: any) { this.selectedTransaction = t; }
  closeTransactionModal() { this.selectedTransaction = null; }

  // kept for legacy compatibility
  @ViewChild(MatPaginator) paginator: MatPaginator;
  dataSource = new MatTableDataSource();
  displayedColumns = ['no', 'transaction_id', 'invoice_no', 'first_name', 'email',
    'transaction_type', 'mobile_no', 'amount', 'status', 'date_createdd'];

  constructor(private router: Router, private service: DashboardService,
              private exportService: ExportService, private elRef: ElementRef) {
    this.userdata = this.service.currentUser();
  }

  ngOnInit() {
    this.getTransactions();
  }

  // ── Data ──────────────────────────────────────────────────
  getTransactions() {
    let payload: any = {};

    if (this.userdata.role === 'ADMIN') {
      payload = { module: 'admin', banca: this.userdata.bankName || '' };
    } else if (this.userdata.role === 'CLIENT') {
      payload = { module: 'client', client_code: this.userdata.code };
    } else if (this.userdata.role === 'AGENT' || this.userdata.role === 'BROKER_ADMIN') {
      payload = { module: 'agent', agent_code: this.userdata.code };
    } else if (this.userdata.role === 'BROKER_USER') {
      payload = { module: 'broker-user', broker_user_code: this.userdata.code };
    }

    this.isLoading = true;
    this.isRefreshing = true;

    this.service.getTransaction(payload)
      .toPromise()
      .then((data: any) => {
        if (data.hasOwnProperty('transactions') && data.responseCode === '000') {
          this.transactions = data.transactions || [];
          this.transactionSum = data.transactionSum && data.transactionSum.length
            ? `GHS ${data.transactionSum[0].total}` : 'GHS 0.00';
          this.applyFilters();
        }
      })
      .catch(() => {
        this.service.swalAlart.fire({
          title: 'Loading failed',
          text: 'Transactions could not be loaded',
          icon: 'error'
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isRefreshing = false;
      });
  }

  // ── Search / Filter ───────────────────────────────────────
  onSearch() { this.currentPage = 0; this.applyFilters(); }
  onStatusFilterChange() { this.currentPage = 0; this.applyFilters(); }
  onTypeFilterChange() { this.currentPage = 0; this.applyFilters(); }

  applyFilters() {
    const q      = (this.searchQuery || '').toLowerCase().trim();
    const status = this.selectedStatusFilter;
    const type   = this.selectedTypeFilter;

    const result = this.transactions.filter(t => {
      const matchSearch = !q || [t.transaction_id, t.invoice_no, t.first_name,
        t.email, t.mobile_no, t.transaction_type, t.status]
        .some(f => f && String(f).toLowerCase().includes(q));
      const matchStatus = !status || (t.status || '').toLowerCase() === status.toLowerCase();
      const matchType   = !type   || (t.transaction_type || '').toLowerCase().includes(type.toLowerCase());
      return matchSearch && matchStatus && matchType;
    });

    this.filteredTransactions = result;
    this.totalItems  = result.length;
    this.totalPages  = Math.ceil(this.totalItems / this.pageSize);
    this.updatePage();
  }

  updatePage() {
    const start = this.currentPage * this.pageSize;
    this.pagedTransactions = this.filteredTransactions.slice(start, start + this.pageSize);
  }

  // ── Pagination ────────────────────────────────────────────
  goToPage(p: number) { this.currentPage = p; this.updatePage(); }
  prevPage() { if (this.currentPage > 0) { this.currentPage--; this.updatePage(); } }
  nextPage() { if (this.currentPage < this.totalPages - 1) { this.currentPage++; this.updatePage(); } }

  getPageNumbers(): number[] {
    const total = this.totalPages;
    const cur   = this.currentPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    if (cur <= 3)          return [0, 1, 2, 3, 4, -1, total - 1];
    if (cur >= total - 4)  return [0, -1, total - 5, total - 4, total - 3, total - 2, total - 1];
    return [0, -1, cur - 1, cur, cur + 1, -1, total - 1];
  }

  // ── Export ────────────────────────────────────────────────
  toggleExportMenu() { this.showExportMenu = !this.showExportMenu; }

  exportAs(format: 'excel' | 'csv' | 'pdf') {
    this.showExportMenu = false;
    const data = this.filteredTransactions.length ? this.filteredTransactions : this.transactions;
    if (!data || data.length === 0) { return; }
    const filename = 'transactions_' + new Date().toISOString().slice(0, 10);
    this.exportService.export(format, ExportService.TRANSACTIONS, data, filename);
  }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (['success', 'paid', 'completed'].includes(s)) return 'status-success';
    if (['pending', 'processing'].includes(s))        return 'status-pending';
    if (['failed', 'error', 'declined'].includes(s))  return 'status-failed';
    return 'status-default';
  }

  getTypeBadgeClass(type: string): string {
    const t = (type || '').toLowerCase();
    if (t.includes('momo') || t.includes('mobile')) return 'type-momo';
    if (t.includes('card') || t.includes('debit'))  return 'type-card';
    if (t.includes('cash'))                         return 'type-cash';
    if (t.includes('cheque'))                       return 'type-cheque';
    return 'type-default';
  }

  get showingFrom(): number { return this.totalItems === 0 ? 0 : this.currentPage * this.pageSize + 1; }
  get showingTo():   number { return Math.min((this.currentPage + 1) * this.pageSize, this.totalItems); }

  // legacy binding (unused in new template)
  applyFilter(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.onSearch();
  }
}
